// CC-042 (2026-09-24) — Every AI-generated summary block on the
// candidate page was rendering the full paragraph inline, making the
// page dense to scan (Full Candidate Summary, each OBP scored
// criterion rationale, resume/transcript AI summaries, per-question
// AI notes). This shared helper shows a 1-2 sentence preview by
// default and reveals the full text on click. Kept as a single
// component so future summary surfaces get consistent behaviour.
//
// Behaviour:
//   - Text ≤ MAX_CHARS → rendered as-is, no toggle.
//   - Text > MAX_CHARS → preview cut at nearest sentence boundary
//     within [MAX_CHARS/2, MAX_CHARS], falling back to word boundary.
//   - Toggle button starts a new line so it never gets confused with
//     the summary copy itself; keeps focus ring visible.
//   - Renders as inline `<span>` so it slots into `<p>` wrappers
//     already used by callers.

import { useState } from "react";

const DEFAULT_MAX_CHARS = 160;

export function briefPreview(text, maxChars = DEFAULT_MAX_CHARS) {
  if (!text) return "";
  const trimmed = String(text).trim();
  if (trimmed.length <= maxChars) return trimmed;
  const slice = trimmed.slice(0, maxChars);
  const lastPeriod = Math.max(
    slice.lastIndexOf(". "),
    slice.lastIndexOf(".\n"),
    slice.lastIndexOf("! "),
    slice.lastIndexOf("? "),
  );
  if (lastPeriod > maxChars * 0.4) {
    return slice.slice(0, lastPeriod + 1);
  }
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice) + "…";
}

export default function CollapsibleSummary({
  text,
  maxChars = DEFAULT_MAX_CHARS,
  testId,
  className = "",
  toggleClassName = "",
}) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const trimmed = String(text).trim();
  if (!trimmed) return null;
  const needsToggle = trimmed.length > maxChars;
  const shown = expanded || !needsToggle
    ? trimmed
    : briefPreview(trimmed, maxChars);

  return (
    <span
      className={`whitespace-pre-wrap ${className}`}
      data-testid={testId}
      data-expanded={needsToggle ? (expanded ? "true" : "false") : undefined}
    >
      {shown}
      {needsToggle && (
        <>
          {" "}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className={`text-[#2563eb] hover:underline focus:outline-none focus:ring-2 focus:ring-[#93c5fd] rounded-sm text-xs font-medium ml-0.5 ${toggleClassName}`}
            data-testid={testId ? `${testId}-toggle` : undefined}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        </>
      )}
    </span>
  );
}
